import React from "react";
import { CircleQuestionMark } from "lucide-react";


type HelpModalProps = {
    open: boolean;
    onClose: () => void;
};

const DynamicCostHelpModal = ({ open, onClose }: HelpModalProps) => {
    if (!open) return null;
    if (!open) return null;

    return (
        <div className="dc-overlay">
            <div className="dc-modal">
                {/* Close Button */}
                <button className="dc-close-btn" onClick={onClose}>
                    ✕
                </button>

                {/* Header */}
                <div className="dc-header">
                    <CircleQuestionMark className="dc-header-icon" style={{color:"#155dfc"}}/>
                    <h2 className="dc-header-title">Dynamic Cost – Quick Help</h2>
                </div>

                {/* Scrollable Content */}
                <div className="dc-content">

                    {/* Section Item */}
                    <div className="dc-item">
                        <div className="dc-blue-bar"></div>
                        <div>
                            <h4 className="dc-item-title">Base Cost:</h4>
                            <p className="dc-item-text">Flat fee before mileage.</p>
                        </div>
                    </div>

                    <div className="dc-item">
                        <div className="dc-blue-bar"></div>
                        <div>
                            <h4 className="dc-item-title">Distance Covered:</h4>
                            <p className="dc-item-text">Miles included in the base cost.</p>
                        </div>
                    </div>

                    <div className="dc-item">
                        <div className="dc-blue-bar"></div>
                        <div>
                            <h4 className="dc-item-title">Add. Cost / Mile:</h4>
                            <p className="dc-item-text">Charge for miles beyond the covered distance.</p>
                        </div>
                    </div>

                    <div className="dc-item">
                        <div className="dc-blue-bar"></div>
                        <div>
                            <h4 className="dc-item-title">Warehouse (Origin):</h4>
                            <p className="dc-item-text">Starting point used to calculate travel distance.</p>
                        </div>
                    </div>

                    <div className="dc-item">
                        <div className="dc-blue-bar"></div>
                        <div>
                            <h4 className="dc-item-title">Install / Removal / Default:</h4>
                            <p className="dc-item-text">
                                Choose job types + set the default rule.
                            </p>
                        </div>
                    </div>

                    <div className="dc-item">
                        <div className="dc-blue-bar"></div>
                        <div>
                            <h4 className="dc-item-title">Taxable:</h4>
                            <p className="dc-item-text">Enable if this cost should have sales tax applied.</p>
                        </div>
                    </div>

                    <hr />

                    {/* How Realesy Uses This */}
                    <div className="dc-section-title fs-16">
                        <span className="dc-section-icon">🔍</span>
                        How Realesy Uses This Cost
                    </div>

                    <p className="dc-item-text">
                        When a new job is created, Realesy looks at:
                    </p>

                    <ul className="dc-list">
                        <li><span style={{color:"#00a63e"}} className="fs-14">✔</span> Job type (Installation/Removal)</li>
                        <li><span style={{color:"#00a63e"}} className="fs-14">✔</span> Origin Warehouse</li>
                        <li><span style={{color:"#00a63e"}} className="fs-14">✔</span> The default cost setting</li>
                    </ul>

                    <p className="dc-item-text">
                        Then it automatically applies the correct cost rule.
                    </p>

                    <hr />

                    {/* Notes */}
                    <div className="dc-section-title fs-16">
                        <span className="dc-section-icon">💡</span>
                        Helpful Notes
                    </div>

                    <ul className="dc-list" style={{ listStyleType: 'disc', paddingLeft: '1.25rem' }}>
                        <li>You can disable old pricing without deleting it using Status.</li>
                        <li>Use clear names like "Standard Install", "Long Distance Removal", etc.</li>
                        <li>
                            Vendors operating in multiple states or zones should create separate
                            rules per warehouse.
                        </li>
                    </ul>
                </div>

                {/* Footer Button */}
                <div className="dc-footer">
                    <button className="dc-btn" onClick={onClose}>
                        Got it!
                    </button>
                </div>
            </div>
            <style>
                {`
         /* Overlay */
.dc-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* Modal Box */
.dc-modal {
  background: #ffffff;
  width: 630px;
  max-height: 86vh;
  border-radius: 14px;
  padding: 0;
  position: relative;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Close Button */
.dc-close-btn {
  position: absolute;
  top: 14px;
  right: 20px;
  font-size: 20px;
  background: none;
  border: none;
  cursor: pointer;
}

/* Header */
.dc-header {
  display: flex;
  align-items: center;
  padding: 24px 32px 0;
  gap: 10px;
}

.dc-header-icon {
  font-size: 24px;
}

.dc-header-title {
  font-size: 22px;
  font-weight: 600;
  margin: 0;
}

/* Scroll Content */
.dc-content {
  padding: 20px 30px;
  overflow-y: auto;
  flex: 1;
}

/* Blue Bar Left */
.dc-item {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.dc-blue-bar {
  width: 4px;
  background: #2563eb;
  border-radius: 4px;
}

/* Titles */
.dc-item-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.dc-item-text {
  margin: 4px 0;
  color: #555;
}

/* Section Title */
.dc-section-title {
  margin: 20px 0 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dc-section-icon {
  font-size: 20px;
}

/* List */
.dc-list {
  list-style: none;
  padding-left: 0;
}

.dc-list li {
  margin-bottom: 6px;
  color: #333;
}

/* Footer */
.dc-footer {
  padding: 16px 32px;
  border-top: 1px solid #eee;
}

.dc-btn {
  width: 100%;
  padding: 12px 0;
  background: #2563eb;
  border: none;
  color: white;
  font-size: 16px;
  border-radius: 8px;
  cursor: pointer;
}
  .dc-btn:hover {
    background: #1447e6 !important;
  }

            `}
            </style>
        </div>
    );
};

export default DynamicCostHelpModal;
